import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';
import * as React from 'react';
import scrollIntoView from 'scroll-into-view';

import { circOut } from '@popmotion/easing';

import TypingIndicator from '../components/TypingIndicator';
import { useChatState } from '../store';
import MessageGroup, { ChatMessageGroup } from './MessageGroup';

interface ChatHistoryProps {
	history?: ChatMessageGroup[];
	showTypingIndicator?: boolean;
	setConversationScrollerWidth: (width: number) => void;
	paddingBottomOffset?: number;
	shouldAutoScroll?: boolean;
}

const visibleMessageGroups = (messageGroup: ChatMessageGroup) =>
	messageGroup.visualCues && messageGroup.visualCues.isRendered ? true : false;

const resizeObserverExists = window.ResizeObserver;

export const ChatHistory: React.FC<ChatHistoryProps> = ({
	history,
	showTypingIndicator = false,
	setConversationScrollerWidth,
	paddingBottomOffset = 0,
	shouldAutoScroll = true,
}) => {
	const { breakpoint } = useChatState();
	const conversationRef = React.useRef<HTMLDivElement>(null);
	const conversationScrollerRef = React.useRef<HTMLDivElement>(null);
	const typingIndicatorRef = React.useRef<HTMLDivElement>(null);
	const lastMessageGroupRef = React.useRef<HTMLDivElement>(null);
	const messageGroupCount = history
		? history.filter(visibleMessageGroups).length
		: 0;
	const [shouldSmoothScroll, setShouldSmoothScroll] = React.useState(false);

	React.useEffect(() => {
		if (shouldAutoScroll) {
			if (!shouldSmoothScroll && conversationRef && conversationRef.current) {
				setShouldSmoothScroll(true);

				// Jump straight to the bottom on initial render
				conversationRef.current.scrollTop =
					conversationRef.current.scrollHeight;
			} else if (
				shouldSmoothScroll &&
				showTypingIndicator &&
				typingIndicatorRef &&
				typingIndicatorRef.current
			) {
				// Scroll smoothly to top of the typing indicator
				scrollIntoView(typingIndicatorRef.current, {
					time: 1000,
					ease: circOut,
					align: {
						top: 0,
					},
					validTarget: (target) => {
						return target === conversationRef.current;
					},
				});
			} else if (
				shouldSmoothScroll &&
				lastMessageGroupRef &&
				lastMessageGroupRef.current
			) {
				// Scroll smoothly to top of the last message group
				scrollIntoView(lastMessageGroupRef.current, {
					time: 1000,
					ease: circOut,
					align: {
						top: 0,
					},
					validTarget: (target) => {
						return target === conversationRef.current;
					},
				});
			}
		}
		// history needs to be a dependency to make this trigger whenever a new message is added
	}, [history, shouldAutoScroll, shouldSmoothScroll, showTypingIndicator]);

	React.useEffect(() => {
		if (breakpoint === 'mobile') {
			if (conversationRef.current) {
				disableBodyScroll(conversationRef.current, {
					reserveScrollBarGap: true,
				});
			}
		} else {
			clearAllBodyScrollLocks();
		}

		return () => {
			clearAllBodyScrollLocks();
		};
	}, [breakpoint]);

	React.useEffect(() => {
		if (!resizeObserverExists) {
			return () => null;
		}

		const conversationScrollerEl = conversationScrollerRef.current;
		let resizeObserver: ResizeObserver;
		if (conversationScrollerEl) {
			resizeObserver = new ResizeObserver(() => {
				setConversationScrollerWidth(conversationScrollerEl.clientWidth);
			});

			resizeObserver.observe(conversationScrollerEl);
		}

		return () => {
			if (conversationScrollerEl) {
				resizeObserver.unobserve(conversationScrollerEl);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div data-testid="chat-history" className="chat-history" aria-live="polite">
			<div ref={conversationRef} className="chat-history__conversations">
				<div
					ref={conversationScrollerRef}
					className="chat-history__conversations-scroller"
					style={{ paddingBottom: `${paddingBottomOffset}px` }}
				>
					{history &&
						history
							.filter(visibleMessageGroups)
							.map((messageGroup, groupIndex) => (
								<MessageGroup
									/* eslint-disable-next-line react/no-array-index-key */
									key={`messageGroup${groupIndex}`}
									innerRef={
										groupIndex === messageGroupCount - 1
											? lastMessageGroupRef
											: undefined
									}
									groupData={messageGroup}
									className="c-chat-message-group flex flex-col my-4"
								/>
							))}

					{showTypingIndicator && (
						<TypingIndicator innerRef={typingIndicatorRef} />
					)}
				</div>
			</div>
		</div>
	);
};

export default ChatHistory;
